import { useMemo, useState } from "react";
import { useFilterStoreWithDayjsDates } from "../../store/filterStore.js";
import { dimensionById, TOTAL_DIMENSION } from "./analyzerDimensions";
import { autoInterval } from "./bucketing";

const GRANULARITIES = { Day: "MMM D", Week: "YYYY-WW", Month: "MMM YYYY", Quarter: "YYYY-Q", Year: "YYYY" };

// fieldsFrom — the set of flat-dim grouping fields a drill path's crumbs
// added (node crumbs from a perspective drill carry `ownerNodeId`, never
// `field`, so they're naturally excluded). Shared by setDimension (strips
// every drilled field on a dimension switch) and drillUp (strips only the
// fields the crumbs being popped added) — same provenance-based computation,
// different crumb subset. Pure/no I/O.
export function fieldsFrom(crumbs) {
  return new Set((crumbs || []).map((c) => c.field).filter(Boolean));
}

// LINK1 — pure URL (de)serialization for the analyzer's scope. No React, no
// store access: scopeToSearchParams/searchParamsToScope round-trip a plain
// { from, to, dim, budget, filters } object through a compact query-string
// shape so the analyzer can be deep-linked (Analyzer.jsx applies it
// on-mount; InvestigationDetailPage's "Explore in analyzer" button builds it
// with scopeToSearchParams directly).
//
// filters encode as `field1:v1,v2;field2:v3`, each field/value individually
// encodeURIComponent'd so reserved separators (`,` `:` `;`) inside a field
// or value survive the round trip without corrupting the split.
//
// Task 8b — ownerFilters (perspective filters, one entry per dimensionCode
// with its selected owner nodes) get a SEPARATE `owner` param, encoded as
// `dimCode:id~label,id~label;dimCode2:...`. Every dimCode/id/label is
// individually encodeURIComponent'd so `:` `,` `;` inside a label survive;
// `~` is left unescaped by encodeURIComponent, but that's harmless here
// because decoding only ever splits each node pair on its FIRST `~` (the one
// we inserted right after the id) — a literal `~` inside the label just
// rides along in the remainder. This is a separate wire from `filters`
// entirely, so flat-filter encoding above is untouched.
export function scopeToSearchParams({ from, to, dim, budget, filters, ownerFilters } = {}) {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;
  if (dim && dim !== "total") params.dim = dim;
  if (budget !== undefined && budget !== null && budget !== "") params.budget = String(budget);
  const encodedFilters = (filters || [])
    .filter((f) => f && f.field && Array.isArray(f.values) && f.values.length > 0)
    .map((f) => `${encodeURIComponent(f.field)}:${f.values.map((v) => encodeURIComponent(v)).join(",")}`)
    .join(";");
  if (encodedFilters) params.filters = encodedFilters;
  const encodedOwner = (ownerFilters || [])
    .filter((f) => f && f.dimensionCode && Array.isArray(f.nodes) && f.nodes.length > 0)
    .map((f) => `${encodeURIComponent(f.dimensionCode)}:${f.nodes.map((n) => `${encodeURIComponent(n.id)}~${encodeURIComponent(n.label)}`).join(",")}`)
    .join(";");
  if (encodedOwner) params.owner = encodedOwner;
  return params;
}

// splitFilters — Task 8: separates the applied filter set into cost-base filters (the
// existing `filters` wire, unchanged {field, values} shape) and perspective/ownership
// scopes (the new `ownerScopes` wire). A filter is a perspective scope only when
// `kind === "perspective"` (the add-filter popover's AnalyzerFilterBar.buildFilter sets
// this explicitly; anything else — `kind: "flat"`, `kind` omitted, deep-linked filters —
// is a cost-base filter). Perspective filters are grouped by dimensionCode, folding
// duplicates into one entry and unioning their node ids (deduped), since the backend's
// ownerScopes shape is one entry per dimension, not one per filter. Pure/no I/O.
export function splitFilters(filters) {
  const list = filters || [];
  const costFilters = list
    .filter((f) => f.kind !== "perspective")
    .map((f) => ({ field: f.category, values: f.selectedValues || [] }));

  const nodeIdsByDimensionCode = new Map();
  list.filter((f) => f.kind === "perspective").forEach((f) => {
    const ids = nodeIdsByDimensionCode.get(f.dimensionCode) || new Set();
    (f.selectedValues || []).forEach((v) => ids.add(Number(v)));
    nodeIdsByDimensionCode.set(f.dimensionCode, ids);
  });
  const ownerScopes = Array.from(nodeIdsByDimensionCode.entries())
    .map(([dimensionCode, ids]) => ({ dimensionCode, nodeIds: Array.from(ids) }));

  return { costFilters, ownerScopes };
}

export function searchParamsToScope(params) {
  const get = params && typeof params.get === "function" ? (k) => params.get(k) : (k) => (params ? params[k] : undefined);

  const from = get("from") || undefined;
  const to = get("to") || undefined;
  const dim = get("dim") || undefined;
  const budgetRaw = get("budget");
  const budget = budgetRaw !== undefined && budgetRaw !== null ? budgetRaw : "";

  const filtersRaw = get("filters");
  const filters = filtersRaw
    ? filtersRaw.split(";").filter(Boolean).map((part) => {
        const sep = part.indexOf(":");
        const field = decodeURIComponent(sep === -1 ? part : part.slice(0, sep));
        const valuesStr = sep === -1 ? "" : part.slice(sep + 1);
        const values = valuesStr ? valuesStr.split(",").filter(Boolean).map((v) => decodeURIComponent(v)) : [];
        return { field, values };
      })
    : [];

  const ownerRaw = get("owner");
  const ownerFilters = ownerRaw
    ? ownerRaw.split(";").filter(Boolean).map((part) => {
        const sep = part.indexOf(":");
        const dimensionCode = decodeURIComponent(sep === -1 ? part : part.slice(0, sep));
        const nodesStr = sep === -1 ? "" : part.slice(sep + 1);
        const nodes = nodesStr
          ? nodesStr.split(",").filter(Boolean).map((pair) => {
              const nSep = pair.indexOf("~");
              const id = decodeURIComponent(nSep === -1 ? pair : pair.slice(0, nSep));
              const label = nSep === -1 ? "" : decodeURIComponent(pair.slice(nSep + 1));
              return { id, label };
            })
          : [];
        return { dimensionCode, nodes };
      })
    : [];

  return { from, to, dim, budget, filters, ownerFilters };
}

export function useAnalyzerScope() {
  const store = useFilterStoreWithDayjsDates();
  const { startDate, endDate, filters, setFilters,
    analyzerDimension, setAnalyzerDimension, analyzerDrillPath = [], setAnalyzerDrillPath,
    analyzerBudgetId = "", setAnalyzerBudgetId } = store;

  const dimension = dimensionById(analyzerDimension?.id) || TOTAL_DIMENSION;
  const dateGranularity = analyzerDimension?.dateGranularity || "Day";

  // Task 6: cost-recognition basis + shadow-cost toggle for the spend-series
  // request. showShadow is force-disabled client-side (in the request) once the scope is
  // owner-scoped (a perspective filter/dimension) — see isOwnerScoped below
  // and its use in buildSpendSeriesRequest's withShadow.
  const [basis, setBasis] = useState("incurred_amortized");
  const [showShadow, setShowShadow] = useState(false);

  // The time-series carries two INDEPENDENT settings, deliberately not one
  // selector: `interval` is aggregation granularity (how wide a bucket is),
  // `cumulative` is display mode (whether buckets accumulate). Collapsing them
  // would make Monthly + Cumulative — the YTD pacing view — unreachable.
  const [interval, setInterval] = useState("auto");
  const [cumulative, setCumulative] = useState(false);

  // "Auto" is STICKY: it stays selected and re-picks whenever the range changes,
  // so drag-zooming from a quarter to five days switches Month to Day without the
  // user touching the control. That only works because the resolved value is
  // DERIVED here rather than stored — storing it would need an effect that fights
  // the user's own clicks. The threshold rule itself lives in bucketing.js, pure
  // and directly testable.
  const resolvedInterval = interval === "auto" ? autoInterval(startDate, endDate) : interval;

  // isOwnerScoped — true when the current scope is narrowed to an owner
  // subtree (a perspective filter is active, or the grouping dimension
  // itself is a perspective). Shadow-cost comparison isn't meaningful once
  // scoped to an owner node, so buildSpendSeriesRequest forces withShadow
  // off in that case; also exposed so the shadow-toggle UI can disable
  // itself.
  const isOwnerScoped = useMemo(() => {
    const { ownerScopes } = splitFilters(filters);
    return ownerScopes.length > 0 || dimension.kind === "perspective";
  }, [filters, dimension]);

  // perspectiveParentId is the owner-node id of the last perspective drill
  // crumb (node crumbs carry `ownerNodeId`; flat-dim crumbs never do), or
  // null at the top level / for non-perspective dimensions. Feeds
  // usePerspectiveSeries's parentOwnerNodeId.
  const perspectiveParentId = useMemo(() => {
    if (dimension.kind !== "perspective") return null;
    for (let i = analyzerDrillPath.length - 1; i >= 0; i--) {
      if (analyzerDrillPath[i].ownerNodeId != null) return analyzerDrillPath[i].ownerNodeId;
    }
    return null;
  }, [dimension, analyzerDrillPath]);

  // A single line when grouping is Total, or a drill/filter narrows a flat
  // dimension to one value. Perspectives are ALWAYS stacked (multiple owner
  // nodes side by side, even at a drilled level) — node-scoped forecast is
  // out of scope for P2 (see plan risk note), so a perspective never shows
  // the single-series actual+fan view, only the stacked breakdown.
  const isSingleSeries = useMemo(() => {
    if (dimension.kind === "perspective") return false;
    if (dimension.id === "total") return true;
    const f = filters.find((x) => x.category === dimension.groupingField);
    return !!(f && f.selectedValues?.length === 1) || analyzerDrillPath.some((c) => c.field != null);
  }, [dimension, filters, analyzerDrillPath]);

  // Switching dimension must not leak the previous dimension's drill state:
  // a stale drill crumb/filter would otherwise misrepresent the new
  // dimension's scope (forced single-series fan on a flat dim, or a
  // silently-narrowed perspective query). Reset the drill path and remove
  // only filters that drilling itself added — provenance-based (mirrors
  // drillUp), not a category match against the outgoing dimension's
  // groupingField: a user's own sidebar filter on that same field, who never
  // drilled, must survive the switch.
  const setDimension = (id) => {
    const drilledFields = fieldsFrom(analyzerDrillPath);
    if (drilledFields.size > 0) setFilters(filters.filter((f) => !drilledFields.has(f.category)));
    setAnalyzerDrillPath([]);
    setAnalyzerDimension({ ...(dimensionById(id) || TOTAL_DIMENSION), dateGranularity });
  };
  const setDateGranularity = (g) => { setAnalyzerDimension({ ...analyzerDimension, dateGranularity: g }); };

  // Drilling adds a filter on the current grouping field and records the crumb.
  const drillInto = (value) => {
    if (!dimension.groupingField) return;
    const cur = filters;
    const exists = cur.find((x) => x.category === dimension.groupingField);
    setFilters(exists
      ? cur.map((x) => x.category === dimension.groupingField ? { ...x, selectedValues: [value] } : x)
      : [...cur, { category: dimension.groupingField, categoryDisplay: dimension.label, label: "", values: [], selectedValues: [value], key: dimension.groupingField }]);
    setAnalyzerDrillPath([...(analyzerDrillPath || []), { field: dimension.groupingField, value }]);
  };

  // drillIntoNode pushes an owner-node crumb for a perspective drill (no
  // filter to set — perspective-series is scoped server-side via
  // perspectiveParentId, computed from this crumb). Node crumbs carry
  // `ownerNodeId`/`label` instead of a flat crumb's `field`/`value`.
  const drillIntoNode = (nodeId, label) => {
    setAnalyzerDrillPath([...(analyzerDrillPath || []), { ownerNodeId: nodeId, label }]);
  };

  const drillUp = (toIndex) => {
    const kept = (analyzerDrillPath || []).slice(0, toIndex);
    const dropped = (analyzerDrillPath || []).slice(toIndex);
    // Only flat crumbs carry a `field` that maps to an active filter to
    // strip; node crumbs (perspective drill) have none — truncating the
    // path is the whole operation for those.
    const droppedFields = fieldsFrom(dropped);
    setFilters(filters.filter((x) => !droppedFields.has(x.category)));
    setAnalyzerDrillPath(kept);
  };

  const buildCloudCostRequest = () => {
    // Defensive: this legacy request has no ownerScopes wire, so a perspective filter
    // (category = dimensionCode, e.g. "organisation") must never ride along as a bogus
    // cost-base `field` — drop it here rather than sending it to /get-cloud-cost.
    const costOnlyFilters = (filters || []).filter((f) => f.kind !== "perspective");
    const reqFilters = costOnlyFilters.map((f) => ({ field: f.category, operator: "=", values: f.selectedValues, is_tag: f.isTag }));
    const grouping = dimension.groupingField ? [dimension.groupingField] : [];
    return {
      measures: [{ name: "measure", formula: "sum(field.direct_cost)" }],
      filters: reqFilters,
      grouping,
      start_date: startDate.format("YYYY-MM-DD"),
      end_date: endDate.format("YYYY-MM-DD"),
      group_by_date: true,
      add_grouped_results: true,
      add_detailed_results: true,
      date_granularity: dateGranularity,
    };
  };

  // buildSpendSeriesRequest — the flat/total spend chart's request to
  // POST /api/v1/analyzer/spend-series (AI-inclusive allocation_cost_base_daily),
  // replacing /get-cloud-cost for that path. groupingField "" (not null) means
  // total — the backend allowlist treats "" as "no group column" (AI2 plan).
  // Flat dims carry their real cost-base column (service_name/sku_description);
  // ai_model rides sku_description (see analyzerDimensions.js). Perspectives
  // never call this (they use usePerspectiveSeries instead).
  const buildSpendSeriesRequest = () => {
    const { costFilters, ownerScopes } = splitFilters(filters);
    return {
      groupingField: dimension.groupingField || "",
      filters: costFilters,
      ownerScopes,
      from: startDate.format("YYYY-MM-DD"),
      to: endDate.format("YYYY-MM-DD"),
      basis,
      withShadow: showShadow && !isOwnerScoped,
    };
  };

  // buildAttributionRequest — the "which dimension moved the number most"
  // table's request to POST /api/v1/analyzer/attribution. Mirrors
  // buildSpendSeriesRequest's {from, to, filters} shape exactly, minus
  // groupingField: attribution ranks movers across ALL dimensions
  // server-side, it isn't scoped to the analyzer's single current grouping.
  // budgetId is passed through as-is: "" (no budget selected) tells the
  // backend to compute whole-project attribution; a real budget id scopes
  // it to that budget's rollup-backed attribution (D5).
  const buildAttributionRequest = () => {
    const { costFilters, ownerScopes } = splitFilters(filters);
    return {
      from: startDate.format("YYYY-MM-DD"),
      to: endDate.format("YYYY-MM-DD"),
      filters: costFilters,
      ownerScopes,
      budgetId: analyzerBudgetId || "",
    };
  };

  // A perspective drilled to an owner node scopes the preview seed to that
  // node (owner_node scope) so the backend can build a node-consistent
  // forecast fan; every other case (perspective at top level, flat/total)
  // keeps the total scope — flat drills narrow via `filters` instead.
  const buildPreviewSeed = () => {
    const isPerspective = dimension.kind === "perspective";
    const scope = (isPerspective && perspectiveParentId != null)
      ? { kind: "owner_node", dimensionCode: dimension.dimensionCode, ref: String(perspectiveParentId) }
      : { kind: "total" };
    const { costFilters, ownerScopes } = splitFilters(filters);
    return {
      questionType: "total_spend",
      metric: "cost",
      scope,
      period: { from: startDate.format("YYYY-MM-DD"), to: endDate.format("YYYY-MM-DD") },
      baseline: "prior_window",
      driverDimension: dimension.driverDimension || "sku",
      filters: costFilters,
      ownerScopes,
    };
  };

  return { dimension, drillPath: analyzerDrillPath, dateGranularity, isSingleSeries, filters, perspectiveParentId,
    budgetId: analyzerBudgetId, setBudgetId: setAnalyzerBudgetId, period: startDate.format("YYYY-MM"),
    basis, setBasis, showShadow, setShowShadow, isOwnerScoped, interval, setInterval, cumulative, setCumulative, resolvedInterval,
    setDimension, setDateGranularity, drillInto, drillIntoNode, drillUp, buildCloudCostRequest, buildSpendSeriesRequest, buildAttributionRequest, buildPreviewSeed, GRANULARITIES };
}
